import type { VatProfile } from '@jfh/intake-shared';
import { parseVatNumber, vatNotFoundMessage } from '@jfh/intake-shared';
import type { StatusTone } from './company.js';

/** The outcome of the public VAT lookup (F8 P2). */
export type VatLookupOutcome =
  | { kind: 'found'; vat: VatProfile }
  | { kind: 'not_found' }
  | { kind: 'busy' }
  | { kind: 'unavailable' };

/** Where a VAT field's HMRC check has got to, for one UK number. */
export type VatState =
  | { kind: 'checking'; number: string }
  | { kind: 'found'; number: string; vat: VatProfile }
  | { kind: 'not_found'; number: string }
  | { kind: 'busy'; number: string }
  | { kind: 'unavailable'; number: string };

/** The UK number to check (`GB553557881`), or null while partial / not UK. */
export function checkableVat(raw: unknown): { vrn: string; display: string } | null {
  const p = parseVatNumber(raw);
  return p?.kind === 'uk' ? { vrn: p.vrn, display: p.display } : null;
}

export function vatStateFromLookup(number: string, r: VatLookupOutcome): VatState {
  return r.kind === 'found' ? { kind: 'found', number, vat: r.vat } : { kind: r.kind, number };
}

/** The line shown under the VAT input. */
export function vatStatusLine(raw: unknown, s: VatState | undefined): { tone: StatusTone; text: string } | null {
  const parsed = parseVatNumber(raw);
  if (!parsed) return null;
  if (parsed.kind === 'other') {
    // Still typing a UK number, or an overseas one HMRC can't check.
    return /^(GB|XI)?\d{0,11}$/.test(parsed.display)
      ? null
      : { tone: 'info', text: 'We can only check UK VAT numbers with HMRC, so we’ll check this one ourselves.' };
  }
  switch (s?.kind) {
    case undefined:
    case 'checking':
      return { tone: 'info', text: 'Checking with HMRC…' };
    case 'found':
      return { tone: 'ok', text: `Registered to ${s.vat.name}.` };
    case 'not_found':
      return { tone: 'error', text: vatNotFoundMessage(s.number) };
    case 'busy':
      return { tone: 'warn', text: 'Too many checks. Please wait a minute, then change the number to try again.' };
    case 'unavailable':
      return { tone: 'warn', text: 'We couldn’t reach HMRC just now. You can carry on, and we’ll check the number ourselves.' };
  }
}

/** The error that stops the applicant continuing past a VAT number, or null. */
export function vatFieldError(raw: unknown, s: VatState | undefined): string | null {
  const parsed = parseVatNumber(raw);
  if (!parsed) return null;
  if (parsed.kind === 'other') {
    return /^(GB|XI)\d{0,11}$/.test(parsed.display) || /^\d{1,8}$|^\d{10,11}$/.test(parsed.display)
      ? 'Enter all 9 digits of the VAT number, for example GB123456789.'
      : null;
  }
  switch (s?.kind) {
    case undefined:
    case 'checking':
      return 'Checking with HMRC. Please wait a moment.';
    case 'not_found':
    case 'busy':
      return vatStatusLine(raw, s)!.text;
    default:
      return null; // found, or HMRC unavailable (checked again on submit)
  }
}
